import React, { useContext, useEffect, useMemo, useState } from "react";
import { ColDef } from "ag-grid-community";
import moment from "moment";
import PaginationLayout from "../components/PaginationLayout";
import { GlobalContext } from "../context/Provider";
import henceforthApi from "../utils/henceforthApi";
import AgGridTable from "../components/ui/AgGridTable";

interface WalletTransaction {
  _id: string;
  type: "CREDIT" | "DEBIT";
  amount: number;
  balance_before: number;
  balance_after: number;
  transaction_type: "REFERRAL_REWARD" | "WITHDRAWAL";
  description: string;
  status: "PENDING" | "COMPLETED" | "FAILED" | "CANCELLED";
  created_at: string;
  reference_id?: string;
}

function WalletTransation({ _id }: any) {
  const [page, setPage] = useState<number>(1);
  const [state, setState] = useState<{
    data: { data: WalletTransaction[]; total_count: number };
  }>({
    data: {
      data: [],
      total_count: 0,
    },
  });
  const [loading, setLoading] = useState(true);
  const limit = 10;
  const { authState } = useContext(GlobalContext);

  if (authState.access_token) {
    henceforthApi.setToken(authState.access_token);
  }

  const onChangePagination = (newVal: number) => {
    setPage(Number(newVal));
  };

  const fetchWalletTransactions = async () => {
    setLoading(true);
    try {
      const res = await henceforthApi.User.walletTransactions(
        _id,
        page - 1,
        limit
      );
      setState(res);
    } catch (err) {
      console.error("Failed to fetch wallet transactions", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWalletTransactions();
  }, [page]);

  const columnDefs = useMemo<ColDef[]>(() => {
    return [
      {
        headerName: "Sr No.",
        minWidth: 70,
        maxWidth: 80,
        width: 75,
        valueGetter: (p) => {
          return (page - 1) * limit + (p.node?.rowIndex ?? 0) + 1;
        },
        cellClass: "font-medium text-slate-500",
      },
      {
        headerName: "Type",
        field: "type",
        minWidth: 100,
        width: 110,
        cellRenderer: (p: any) => {
          return p.value === "CREDIT" ? (
            <span className="inline-flex px-2 py-0.5 rounded text-[11px] font-bold uppercase bg-emerald-50 text-emerald-700 border border-emerald-200">
              CREDIT
            </span>
          ) : (
            <span className="inline-flex px-2 py-0.5 rounded text-[11px] font-bold uppercase bg-rose-50 text-rose-700 border border-rose-200">
              DEBIT
            </span>
          );
        },
      },
      {
        headerName: "Transaction Type",
        field: "transaction_type",
        minWidth: 150,
        width: 170,
        cellRenderer: (p: any) => {
          const tx = p.value;
          if (tx === "REFERRAL_REWARD") {
            return (
              <span className="inline-flex px-2 py-0.5 rounded text-[11px] font-semibold bg-sky-50 text-sky-700 border border-sky-200">
                REFERRAL REWARD
              </span>
            );
          }
          if (tx === "WITHDRAWAL") {
            return (
              <span className="inline-flex px-2 py-0.5 rounded text-[11px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">
                WITHDRAWAL
              </span>
            );
          }
          return (
            <span className="inline-flex px-2 py-0.5 rounded text-[11px] font-medium bg-slate-100 text-slate-700">
              {tx ? tx.replace(/_/g, " ") : "N/A"}
            </span>
          );
        },
      },
      {
        headerName: "Amount",
        field: "amount",
        minWidth: 110,
        width: 130,
        cellRenderer: (p: any) => {
          const isCredit = p.data?.type === "CREDIT";
          return (
            <span
              className={`font-bold text-sm ${isCredit ? "text-emerald-700" : "text-rose-600"
                }`}
            >
              {isCredit ? "+" : "-"}
              {p.value !== undefined && p.value !== null ? p.value : "N/A"}
            </span>
          );
        },
      },
      {
        headerName: "Description",
        field: "description",
        minWidth: 200,
        flex: 1.5,
        cellRenderer: (p: any) => {
          const desc = p.value || "N/A";
          return (
            <span className="truncate text-slate-700 text-sm" title={desc}>
              {desc}
            </span>
          );
        },
      },
      {
        headerName: "Status",
        field: "status",
        minWidth: 120,
        width: 130,
        cellRenderer: (p: any) => {
          const status = p.value;
          if (status === "COMPLETED") {
            return (
              <span className="inline-flex px-2 py-0.5 rounded text-[11px] font-bold uppercase bg-emerald-50 text-emerald-700">
                COMPLETED
              </span>
            );
          }
          if (status === "PENDING") {
            return (
              <span className="inline-flex px-2 py-0.5 rounded text-[11px] font-bold uppercase bg-amber-50 text-amber-700">
                PENDING
              </span>
            );
          }
          if (status === "FAILED") {
            return (
              <span className="inline-flex px-2 py-0.5 rounded text-[11px] font-bold uppercase bg-rose-50 text-rose-700">
                FAILED
              </span>
            );
          }
          return (
            <span className="inline-flex px-2 py-0.5 rounded text-[11px] font-medium bg-slate-100 text-slate-700">
              {status ? status.replace(/_/g, " ") : "N/A"}
            </span>
          );
        },
      },
      {
        headerName: "Date",
        field: "created_at",
        minWidth: 120,
        width: 130,
        valueFormatter: (p) => {
          return p.value
            ? moment(Number(p.value)).format("DD MMM YYYY")
            : "N/A";
        },
      },
    ];
  }, [page]);

  return (
    <>
      <AgGridTable
        rowData={state?.data?.data || []}
        columnDefs={columnDefs}
        loading={loading}
        domLayout="autoHeight"
        emptyMessage="No wallet transactions found"
      />

      <div className="mt-3">
        <PaginationLayout
          count={state?.data?.total_count || 0}
          data={state?.data?.data || []}
          page={page}
          limit={Number(limit)}
          loading={loading}
          onPageChange={(val) => onChangePagination(val)}
        />
      </div>
    </>
  );
}

export default WalletTransation;

